import {
    asyncEach,
    ClassList,
    stringToArray,
    isArray,
    isObject,
} from './utils'
import './typing.scss'

class Typing {
    isInitial = true
    isStopping = false // Whether has stopped typing
    isAborting = false // Whether has aborted typing
    // ele
    typingUl$ = null
    typingLi$ = null
    // start timer
    timer = null
    // Status for recovering typing
    status = {
        section: 0,
        sentence: 0,
        word: 0,
        isFore: true,
    }

    /**
     * Generate an instance of Class Typing
     *
     * @param el$ { HTMLElement | string } element node of document , or the string for `document.querySelector`
     *     function
     * @param texts { Array<string|Object|Array> }
     * @param config { Object }
     */
    constructor(el$, texts, config = {}) {
        if (typeof el$ === 'string' && !document.querySelector) {
            throw new Error('Your browser can\'t support `document.querySelector` [caniuse-querySelector](http://caniuse.com/#search=querySelector)!!!')
        }

        this.el$ = (typeof el$ === 'string') ? document.querySelector(el$) : el$

        if (typeof el$ === 'string' && !this.el$) {
            throw new Error('Can\'t find any element node from the selector: \''+ el$ +'\' ')
        }

        this.texts = this.initTexts(texts)
        this.config = {}

        for (const key in Typing._config) {
            this.config[key] = config[key] || Typing._config[key]
        }

        // initDom
        this.initDom()
    }

    /**
     * Format `texts`
     *
     * @param texts { Array<String|Object|Array> }
     * @returns {*}
     */
    initTexts(texts) {
        for (let i = 0, len = texts.length; i < len; i++) {
            const text = texts[i]

            if (typeof text === 'string') {
                texts[i] = {
                    text,
                    words: stringToArray(text),
                    children: [
                        {
                            text,
                            words: stringToArray(text),
                        }
                    ] ,
                }

                continue
            }

            if (isObject(text)) {
                if ('text' in text) {
                    texts[i].words = stringToArray(text.text)
                    texts[i].children = texts[i].children ? [{
                            ...texts[i].children,
                            text: text.text,
                            words: stringToArray(text.text),
                        }] : [{
                            text: text.text,
                            words: stringToArray(text.text),
                        }]

                    continue
                }

                if ('children' in text) {
                    texts[i].children = this.initTexts(texts[i].children)

                    continue
                }
            }

            if (isArray(text)) {
                texts[i] = {
                    children: this.initTexts(text),
                }
            }
        }

        return texts
    }

    /**
     * Append document elements to `el$`
     */
    initDom() {
        const { cssPrefix } = this.config
        const typingUl$ = document.createElement('ul')
        const typingLi$ = document.createElement('li')

        typingUl$.className = this.getTypingUlCls(cssPrefix)
        typingLi$.className = this.getTypingLiCls(cssPrefix)

        typingUl$.appendChild(typingLi$)

        this.typingUl$ = typingUl$
        this.typingLi$ = typingLi$

        this.el$.appendChild(typingUl$)
    }

    /**
     * Iterator for Section
     *
     * @param secInd
     * @returns {Promise.<TResult>}
     */
    iterateSections(secInd = 0) {
        const that = this

        return new Promise((resolve) => {
            asyncEach(this.texts, function (value, index) {
                if (that.isStopping) return

                // normal iterator
                if (index < secInd) return

                // async iterator
                const done = this.async()
                let { sentence, isFore } = that.status

                // record
                that.status.section = index

                // next sentence
                that.iterateSentences(value.children, sentence, isFore, { ...value }).then(() => {
                    done()
                })
            }, function () {
                resolve()
            })
        }).then(() => {
            if (that.config.infinite && !that.isStopping) {
                return that.iterateSections()
            }
        })
    }

    /**
     * Iterator for Sentences
     *
     * @param sentences { Array<Object> } The all `sentences` in current `section`
     * @param senInd { Number } The index of `sentence`
     * @param isForward { Boolean } Whether to go forward
     * @param secValue { Object } Can set styles, className and hooks to section // TODO: add hooks support
     * @returns {Promise.<TResult>}
     */
    iterateSentences(sentences = [], senInd = 0, isForward = true, secValue = {}) {
        const that = this
        const len = sentences.length

        // update section dom
        if ('className' in secValue) {
            this.typingUl$.className = `${this.getTypingUlCls()} ${secValue.className}`
        }

        if ('styles' in secValue) {
            for (const key in secValue.styles) {
                this.typingUl$.style[key] = secValue.styles[key]
            }
        }

        return new Promise((resolve) => {
            asyncEach(sentences, function (value, index) {
                if (that.isStopping) return

                index = isForward ? index : (len - 1 - index)
                value = sentences[index]

                if (isForward && (index < senInd)) return
                if (!isForward && (index > senInd)) return

                const done = this.async()
                let { word } = that.status

                // record
                that.status.sentence = index

                if (isForward) {
                    that.iterateWords(value.words, word, isForward, value).then(() => {
                        const isFinish = that.status.word === value.words.length - 1
                        const isStopping = that.isStopping || that.isAborting

                        if ((index !== (len - 1)) && isFinish) {
                            const nTypingLi$ = document.createElement('li')

                            nTypingLi$.className = that.getTypingLiCls(that.config.cssPrefix)

                            ClassList.remove(that.typingLi$, `${that.config.cssPrefix}-cursor`)
                            that.typingLi$ = nTypingLi$
                            that.typingUl$.appendChild(nTypingLi$)

                            that.status.word = 0
                        }

                        if (isStopping) {
                            if (!isFinish) {
                                that.status.word += 1
                            } else {
                                if (index < (len - 1)) {
                                    that.status.sentence += 1
                                } else {
                                    that.status.isFore = false
                                }
                            }
                        }

                        done()
                    })
                } else {
                    that.iterateWords(value.words, value.words.length - 1, false, value).then(() => {
                        const isFinish = that.status.word === 0

                        if ((index !== 0) && isFinish) {
                            that.typingUl$.removeChild(that.typingLi$)
                            that.typingLi$ = that.typingUl$.lastElementChild
                        }

                        ClassList.add(that.typingLi$, `${that.config.cssPrefix}-cursor`)

                        if ((that.isStopping || that.isAborting) && !isFinish) {
                            that.status.word -= 1
                        }

                        done()
                    })
                }
            }, function () {
                if (!that.isAborting && !that.isStopping) {
                    that.status.isFore = !that.status.isFore
                }

                resolve()
            })
        }).then(() => {
            return new Promise((resolve) => {
                let isStopping = that.isStopping || that.isAborting
                let secs = that.texts
                let sens = secs[secs.length - 1].children
                let words = sens[sens.length - 1].words

                if (
                    that.status.section === (secs.length - 1) &&
                    that.status.sentence === (sens.length - 1) &&
                    that.status.word === (words.length - 1) &&
                    !that.config.infinite
                ) {
                    // 终止
                    that.abort()
                }

                if (isForward && !isStopping) {
                    setTimeout(() => {
                        that.iterateSentences(sentences, sentences.length - 1, false).then(() => {
                            resolve()
                        })
                    }, that.config.sectionDelay)
                } else {
                    resolve()
                }
            })
        })
    }

    /**
     * Iterator for Words
     *
     * @param words {Array<String>} All the words in current sentence.
     * @param wordInd { Number } The index of word
     * @param isForward { Boolean }  Whether to go forward (add word)
     * @param senVal { Object } Can set styles, className and hooks to sentence // TODO: add hooks support
     * @returns {Promise.<TResult>}
     */
    iterateWords(words = [], wordInd = 0, isForward = true, senVal = {}) {
        const that = this
        const len = words.length

        // update sentence dom
        if ('className' in senVal && isForward) {
            ClassList.add(this.typingLi$, senVal.className)
        }

        if ('styles' in senVal && isForward) {
            for (const key in senVal.styles) {
                this.typingLi$.style[key] = senVal.styles[key]
            }
        }

        // hook

        return new Promise((resolve) => {
            asyncEach(words, function (value, index) {
                if (that.isStopping) return

                index = isForward ? index : (len - 1 - index)
                value = words[index]

                if (isForward && (index < wordInd)) return
                if (!isForward && (index > wordInd)) return

                const done = this.async()

                // record
                that.status.word = index

                setTimeout(() => {
                    let text = that.typingLi$.innerText

                    if (isForward) {
                        that.typingLi$.innerText = text + value
                    } else {
                        that.typingLi$.innerText = text.substring(0, text.length - value.length)
                    }

                    done()
                }, isForward ? that.config.typeSpeed : that.config.backSpeed)

            }, function () {
                resolve()
            })
        }).then(() => {
            return new Promise((resolve) => {
                let isStopping = that.isStopping || that.isAborting

                if (isForward && !isStopping) {
                    setTimeout(() => {
                        resolve()
                    }, that.config.sentenceDelay)
                } else {
                    resolve()
                }
            })
        })
    }

    // continue running
    run() {
        if (this.isAborting) return false
        if (!this.isStopping && !this.isInitial) return

        this.isStopping = false
        this.isInitial = false

        // prevent memory leaks
        clearTimeout(this.timer)

        this.timer = setTimeout(() => {
            this.iterateSections(this.status.section).then(() => {
                this.isStopping = true
            })
        }, this.isInitial ? this.config.startDelay : 0)
    }

    // pause
    stop() {
        this.isStopping = true
    }

    // abort running, can't recover
    abort() {
        if (this.isAborting) return true

        this.isStopping = true
        this.isAborting = true
    }

    getTypingUlCls(cssPrefix = this.config.cssPrefix) {
        return `${cssPrefix} ${cssPrefix}-section`
    }

    getTypingLiCls(cssPrefix = this.config.cssPrefix) {
        return `${cssPrefix}-sentence ${cssPrefix}-cursor`
    }
}

// Default global configuration
Typing._config = {
    cssPrefix: 'typing', // css prefix
    typeSpeed: 100, // typing interval
    backSpeed: 50, // back interval
    startDelay: 500,
    sectionDelay: 1000,
    sentenceDelay: 50,
    blinkDelay: 20, // interval for blinking of typing cursor
    infiniteDelay: 3000,
    infinite: false,
    // beforeStart: null, // we will call the function before start
    // beforeBack: null, // we will call the function before back
}

/**
 * Override the global configuration
 *
 * @param config
 */
Typing.config = (config = {}) => {
    for (const key in Typing._config) {
        Typing._config[key] = config[key] || Typing._config[key]
    }
}

/**
 * Init and start typing
 *
 * @param el$
 * @param texts
 * @param config
 */
Typing.init = (el$, texts, config) => {
    return new Typing(el$, texts, config)
}

/**
 * Run typing
 *
 * @param typing
 */
Typing.run = (typing) => {
    typing.run()

    return typing
}

/**
 * Stop typing
 * @param typing
 */
Typing.stop = (typing) => {
    typing.stop()

    return typing
}

/**
 * Abort typing
 * @param typing
 */
Typing.abort = (typing) => {
    typing.abort()

    return typing
}

export { Typing }



// WEBPACK FOOTER //
// ./src/typing.js